import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import moment from "moment-timezone";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  MenuItem,
  InputBase,
  Tooltip,
  CircularProgress,
  Divider,
  Menu,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ReactComponent as MoreOptionsIcon } from '../../assets/system-uicons_circle-menu.svg';
import { ReactComponent as ClearSortIcon } from '../../assets/clear-sort.svg';
import { SEVERITY, SEVERITY_MAP_BE, TIMEZONE_MAP } from './Constants';

import {
  getPlatformHealthColumnFilterValues,
  getPlatformHealthDetail,
  fetchPlatformHealthGroupChildren,
} from "../../service/service";
import {
  AlertSeverity,
  AlertSeverityFilter,
  PlatformAlertQueryResponse,
  PlatformAlertRow,
  GroupedAlertData,
  ShowMoreRow
} from "./Alerts.types";
import { PaginationDetails } from "../pagination_details";
import XLSX from "xlsx-js-style";
import {
  severityBadgeColor,
  statusPillColor,
  transformAlerts,
  getHighestSeverity,
} from "./helpers";
import {
  FONT,
  summaryLabelSx,
  summaryValueSx,
} from "./styles";
import { CustomizedDataGrid } from "../customized_data_grid";
import CaptureExportControls from "utils/CaptureExportControls";
import html2canvas from "html2canvas";
import ImagePreview from "utils/ImagePreview";
import { generateDashboardPdf } from "utils/pdfExportUtil";
import type { PdfColumn } from "utils/pdfExportUtil";
import { withShowMoreHandler } from "utils/groupByUtil";
import { GroupedTableFormatter } from "components/Common/GroupedTableFormatter";
import GroupByShowMore from "components/Common/GroupByShowMore";
import { PlatformHealthGroupByDropdown } from "components/Common/PlatformHealthGroupByDropdown";
import { useColumnFilters } from "components/ColumnFilters/useColumnFilters";

type SortDir = "asc" | "desc" | "";

const toColumnFiltersPayload = (filterModel: Record<string, any>, columns: any[]) =>
  Object.entries(filterModel).reduce<any[]>((acc, [id, filter]) => {
    const column = columns.find((item) => item.key === id);
    const condition = filter?.condition;

    // Options the user ticked. Older models put them in `values` when there's no condition.
    const selOptions: any[] = filter?.selOptions ?? (condition ? [] : filter?.values ?? []);

    // Condition values (value1 / value2), dropping empty ones
    const conditionValues = condition
      ? [condition.value1, condition.value2].filter(
          (v) => v !== null && v !== undefined && v !== ""
        )
      : [];

    const hasOptions = selOptions.length > 0;
    const hasCondition = !!condition?.operator;

    // Nothing selected for this column, so don't send it
    if (!hasOptions && !hasCondition) return acc;

    const entry: Record<string, any> = { id: column?.filterField ?? id };
    if (hasOptions) entry.selOptions = selOptions;
    if (hasCondition) {
      entry.operator = condition.operator;
      entry.values = conditionValues;
    }

    acc.push(entry);
    return acc;
  }, []);

function toBackendSeverity(severity: AlertSeverity): string {
  return SEVERITY_MAP_BE[severity];
}

function computeIsFilterChange(
  opts: Partial<{
    page: number;
    severityFilter: AlertSeverityFilter;
    searchText: string;
    pageSize: number;
    sortField: string;
    sortDir: SortDir;
    groupBy: string;
  }> | undefined,
  severityFilter: AlertSeverityFilter,
  searchText: string,
  pageSize: number,
  sortingInfo: { sortField: string; sortDir: SortDir },
  groupBy: string
): boolean {
  return (
    (opts?.severityFilter !== undefined && opts.severityFilter !== severityFilter) ||
    (opts?.searchText !== undefined && opts.searchText !== searchText) ||
    (opts?.pageSize !== undefined && opts.pageSize !== pageSize) ||
    (opts?.sortField !== undefined && opts.sortField !== sortingInfo.sortField) ||
    (opts?.sortDir !== undefined && opts.sortDir !== sortingInfo.sortDir) ||
    (opts?.groupBy !== undefined && opts.groupBy !== groupBy)
  );
}

const OverflowTooltip = ({ text, sx = {} }: { text: string; sx?: any }) => {
  const theme = useTheme();
  const colors = theme.palette;

  const textElementRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textElementRef.current) {
      const { scrollWidth, clientWidth } = textElementRef.current;
      setIsOverflowing(scrollWidth > clientWidth);
    }
  }, [text]);

  return (
    <Tooltip
      title={isOverflowing ? text : ""}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: `${colors.tooltip.background.primary} !important`,
            color: `${colors.tooltip.text.primary} !important`,
            fontFamily: FONT,
            fontSize: "12px",
          },
        },
        arrow: {
          sx: {
            color: `${colors.tooltip.background.primary} !important`,
          },
        },
      }}
    >
      <Typography
        ref={textElementRef}
        sx={{
          display: "block",
          width: "fit-content",
          maxWidth: "85%",
          margin: "0 auto",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: FONT,
          fontSize: "13px",
          color: "#1a2a3a",
          cursor: "default",
          textAlign: "center",
          ...sx,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  const normalized = String(severity || "")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();

  const bg = severityBadgeColor(normalized as any);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "104px",
        padding: "6px 14px",
        borderRadius: "4px",
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: "12px",
        lineHeight: 1,
        color: "#ffffff",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        textAlign: "center",
        boxSizing: "border-box",
        backgroundColor: bg,
      }}
    >
      {normalized.replace(/_/g, " ")}
    </span>
  );
};

function buildAlertRequestBody(
  platformId: string,
  queryParams: {
    nextSearch: string;
    nextPage: number;
    nextPageSize: number;
    nextSortField: string;
    nextSortDir: SortDir;
    nextSeverity: AlertSeverityFilter;
    activeSearchAfterMap: Record<number, any[]>;
    nextGroupBy: string;
    nextColumnFilters: any[];
  },
  timeRange: {
    timeRangeType: string;
    customStartDate?: Date | null;
    customEndDate?: Date | null;
  }
): any {
  const { nextSearch, nextPage, nextPageSize, nextSortField, nextSortDir, nextSeverity, activeSearchAfterMap, nextGroupBy, nextColumnFilters } = queryParams;
  const { timeRangeType, customStartDate, customEndDate } = timeRange;

  const body: any = {
    platformId,
    searchText: nextSearch,
    timeRange: timeRangeType,
    page: nextPage,
    pageSize: nextPageSize,
    sortField: nextSortField || undefined,
    sortDir: nextSortDir || undefined,
    severity: nextSeverity === "ALL" ? undefined : [toBackendSeverity(nextSeverity)],
    searchNext: nextPage > 1 ? activeSearchAfterMap[nextPage - 1] : undefined,
    groupBy: nextGroupBy !== "none" ? nextGroupBy : undefined,
    columnFilters: nextColumnFilters,
  };

  if (timeRangeType === 'today') {
    body.timeRange = {
      startTime: moment.tz('America/Chicago').startOf('day').format('MM-DD-YYYY HH:mm:ss'),
      endTime: moment.tz('America/Chicago').endOf('day').format('MM-DD-YYYY HH:mm:ss'),
    };
  } else if (timeRangeType === 'custom' && customStartDate && customEndDate) {
    body.timeRange = {
      startTime: moment(customStartDate)
        .tz('America/Chicago')
        .startOf('day')
        .format('MM-DD-YYYY HH:mm:ss'),
      endTime: moment(customEndDate)
        .tz('America/Chicago')
        .endOf('day')
        .format('MM-DD-YYYY HH:mm:ss'),
    };
    body.fromDate = moment(customStartDate)
      .tz('America/Chicago')
      .startOf('day')
      .format('MM-DD-YYYY HH:mm:ss');
    body.toDate = moment(customEndDate)
      .tz('America/Chicago')
      .endOf('day')
      .format('MM-DD-YYYY HH:mm:ss');
  }

  return body;
}

function parseAlertResponse(data: any, timeRangeType: string, ianaTimezone: string): PlatformAlertQueryResponse {
  const { platformRes, aggregation, detail, pagination, isGrouped, columnList } = data;

  const summaryObj: any = {};
  aggregation.forEach((item: any) => {
    const status = String(item.status).toLowerCase();
    summaryObj[status] = item.count;
  });

  let alerts: PlatformAlertRow[] = [];
  let groupedData: GroupedAlertData[] = [];
  
  // Process grouped or non-grouped data
  if (isGrouped && detail) {
    groupedData = detail.map((group: any) => ({
      groupByValue: group[Object.keys(group)[0]] || 'dnassigndded',
      groupCount: group[`${Object.keys(group)[0]}Count`] || 0,
      children: transformAlerts(group.children || [], ianaTimezone),
      isParent: true,
      isExpanded: false,
    }));
  } else {
    alerts = transformAlerts(detail, ianaTimezone);
  }

  // --- ADD BACK FALLBACK COLUMN GENERATION ---
  let derivedColumnList = columnList;
  if ((!derivedColumnList || derivedColumnList.length === 0) && detail && detail.length > 0) {
    // Don't generate columns if grouped (use backend columns)
    if (!isGrouped) {
      derivedColumnList = Object.keys(detail[0])
        .filter((k) => !['uuid', 'alertLink', 'incidentLink', 'incidentNo', 'alertAge'].includes(k))
        .map((k) => {
          let cellType = 'text';
          const lowerK = k.toLowerCase();
          if (lowerK.includes('alertno') || lowerK.includes('ticket')) {
            cellType = 'link';
          } else if (lowerK === 'severity') {
            cellType = 'badge';
          } else if (lowerK.includes('time') || lowerK.includes('date')) {
            cellType = 'time';
          }
          
          const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
          
          return {
            key: k,
            label: label,
            type: cellType,
            sortable: k !== 'description',
            width: 240
          };
        });
    }
  }

  return {
    meta: {
      generatedAt: new Date().toISOString(),
      platform: {
        platformId: platformRes.platformId,
        platformName: platformRes.platformName,
        status:
          platformRes.status === "INFO" || !platformRes.status
            ? SEVERITY.INFO
            : (String(platformRes.status).toUpperCase() as AlertSeverity),
        statusColor: "BLUE",
        alertSource: platformRes.alertSource,
      },
      effectiveTimeRange: { type: timeRangeType, from: "", to: "" },
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems,
        totalPages: pagination.totalPages,
      },
    },
    severitySummary: {
      critical: summaryObj.critical || 0,
      major: summaryObj.major || 0,
      minor: summaryObj.minor || 0,
      warning: summaryObj.warning || 0,
      info: summaryObj.ok || 0,
      platformAffected: summaryObj.platform_affected || 0,
      acknowledged: summaryObj.acknowledged || 0,
      unacknowledged: summaryObj.unacknowledged || 0,
    },
    alerts,
    groupedData,
    isGrouped: isGrouped || false,
    columnList: derivedColumnList || [],
  };
}

export default function PlatformAlertsDrilldownModal(props: Readonly<{
  open: boolean;
  platformId: string | null;
  platformName?: string;
  timeRangeType: string;
  customStartDate?: Date | null;
  customEndDate?: Date | null;
  onClose: () => void;
  initialSearchText?: string;
  timezone?: string;
  overrideStatus?: string;
}>) {
  const theme = useTheme();
  const colors = theme.palette;

  const {
    open,
    platformId,
    platformName,
    timeRangeType,
    customStartDate,
    customEndDate,
    onClose,
    initialSearchText,
    timezone = "EST",
    overrideStatus,
  } = props;

  const ianaTimezone = (TIMEZONE_MAP as any)[timezone] || TIMEZONE_MAP.CST;

  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState<PlatformAlertQueryResponse | null>(null);
  const [groupedDisplayData, setGroupedDisplayData] = useState<(GroupedAlertData | PlatformAlertRow | ShowMoreRow)[]>([]);
  const [loadingMoreChildren, setLoadingMoreChildren] = useState<Record<string, boolean>>({});
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<string>("none");
  const columnDetails: Record<string, string> = {};

  const isAzure = useMemo(() => {
    return resp?.meta.platform.alertSource?.toLowerCase() === "azure" || platformName?.toLowerCase().includes("azure");
  }, [resp, platformName]);

  const [severityFilter, setSeverityFilter] = useState<AlertSeverityFilter>("ALL");
  const [searchText, setSearchText] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchError, setSearchError] = useState<string>("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  
  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const [sortingInfo, setSortingInfo] = useState<{
    sortByLabel: string;
    sortField: string;
    sortDir: SortDir;
  }>({ sortByLabel: "", sortField: "", sortDir: "" });

  const [searchAfterMap, setSearchAfterMap] = useState<Record<number, any[]>>({});

  const reqIdRef = useRef(0);
  const columnFiltersPayloadRef = useRef<any[]>([]);

  const ALL_GROUPABLE_FIELDS = [
    { value: "resource", label: "Resource" },
    { value: "severity", label: "Severity" },
    { value: "owner", label: "Owner" },
    { value: "assignmentGroup", label: "Assignment Group" },
    { value: "subscriptionName", label: "Subscription" },
    { value: "resourceGroup", label: "Resource Group" },
  ];

  const groupByOptions = useMemo(() => {
    const options = [{ value: "none", label: "None" }];
    
    if (resp?.columnList && resp.columnList.length > 0) {
      resp.columnList.forEach(column => {
        const matchingField = ALL_GROUPABLE_FIELDS.find(field => field.value === column.key);
        if (matchingField) {
          options.push({
            value: column.key,
            label: column.label
          });
        }
      });
    } else {
      options.push(...ALL_GROUPABLE_FIELDS);
    }
    
    const uniqueOptions = options.reduce((acc, current) => {
      const exists = acc.find(item => item.value === current.value);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, [] as Array<{ value: string; label: string }>);
    
    return uniqueOptions;
  }, [resp?.columnList]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const effectivePlatformName = useMemo(() => {
    return platformName || resp?.meta.platform.platformName || "Platform";
  }, [platformName, resp]);

  const load = async (
    opts?: Partial<{
      page: number;
      severityFilter: AlertSeverityFilter;
      searchText: string;
      pageSize: number;
      sortField: string;
      sortDir: SortDir;
      groupBy: string;
    }>
  ) => {
    if (!platformId) return;

    const nextPage = opts?.page ?? page;
    const nextSeverity = opts?.severityFilter ?? severityFilter;
    const nextSearch = opts?.searchText ?? searchText;
    const nextPageSize = opts?.pageSize ?? pageSize;
    const nextGroupBy = opts?.groupBy ?? groupBy;

    const nextSortField = opts?.sortField ?? sortingInfo.sortField;
    const nextSortDir = opts?.sortDir ?? sortingInfo.sortDir;

    const isFilterChange = computeIsFilterChange(opts, severityFilter, searchText, pageSize, sortingInfo, groupBy);

    if (isFilterChange) {
      setSearchAfterMap({});
      setGroupedDisplayData([]);
    }

    const activeSearchAfterMap = isFilterChange ? {} : searchAfterMap;

    const myReqId = ++reqIdRef.current;

    setLoading(true);
    try {
      const body = buildAlertRequestBody(
        platformId,
        {
          nextSearch,
          nextPage,
          nextPageSize,
          nextSortField,
          nextSortDir,
          nextSeverity,
          activeSearchAfterMap,
          nextGroupBy,
          nextColumnFilters: columnFiltersPayloadRef.current,
        },
        { timeRangeType, customStartDate, customEndDate }
      );

      const response = await getPlatformHealthDetail(body);
      if (myReqId !== reqIdRef.current) return;

      if (response?.success && response.data) {
        const { searchAfter } = response.data;
        const finalRes = parseAlertResponse(response.data, timeRangeType, ianaTimezone);

        setResp(finalRes);
        setSeverityFilter(nextSeverity);
        setSearchText(nextSearch);
        setPageSize(nextPageSize);
        setPage(nextPage);
        setGroupBy(nextGroupBy);
        setSortingInfo((prev) => ({
          sortByLabel: prev.sortByLabel,
          sortField: nextSortField || "",
          sortDir: (nextSortDir as any) || "",
        }));

        if (searchAfter) {
          setSearchAfterMap((prev) =>
            isFilterChange ? { [nextPage]: searchAfter } : { ...prev, [nextPage]: searchAfter }
          );
        }
      } else {
        setResp(null);
      }
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      console.error("Failed to load platform health detail:", e);
      setResp(null);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  const lastLoadKeyRef = useRef<string>("");

  useEffect(() => {
    if (!open || !platformId) {
      lastLoadKeyRef.current = "";
      return;
    }

    const loadKey = `${platformId}__${timeRangeType}__${groupBy}`;
    if (lastLoadKeyRef.current === loadKey) return;
    lastLoadKeyRef.current = loadKey;

    const effectiveSearch = initialSearchText?.trim() || "";

    setSeverityFilter("ALL");
    setSearchText(effectiveSearch);
    setSearchDraft(effectiveSearch);
    setSearchError("");
    setPage(1);
    setPageSize(10);
    setResp(null);
    setSortingInfo({ sortByLabel: "", sortField: "", sortDir: "" });
    setSearchAfterMap({});

    load({
      page: 1,
      pageSize: 10,
      severityFilter: "ALL",
      searchText: effectiveSearch,
      sortField: "",
      sortDir: "",
      groupBy: groupBy,
    });
  }, [open, platformId, timeRangeType, groupBy]);

  const handleGroupByChange = (newGroupBy: string) => {
    setGroupBy(newGroupBy);
    setPage(1);
    setGroupedDisplayData([]);
    load({ page: 1, groupBy: newGroupBy });
  };

  const getSortValue = (r: any, field: string) => {
  switch (field) {
    case "alertNo": return r.alertId;
    case "resource": return r.resource?.resourceName;
    case "severity": return r.severity;
    case "issue": return r.issue?.summary;
    case "description": return r.description;
    case "alertTime": return r.durationLabel;
    case "owner": return r.owner?.team;
    case "ticket": return r.ticket?.ticketId;
    case "assignmentGroup": return r.owner?.assignmentGroup;
    case "state": return r.state;
    case "resourceGroup": return r.resourceGroup;
    case "subscriptionId": return r.subscriptionId;
    case "subscriptionName": return r.subscriptionName;
    default: {
      // Fallback to previous logic for dynamic fields
      let val = r[field];
      if (val === undefined) {
        if (field === 'alertNo') val = r.alertId;
        else if (field === 'alertTime') val = r.durationLabel;
        else if (field === 'owner') val = r.owner?.team;
        else if (field === 'assignmentGroup') val = r.owner?.assignmentGroup;
        else if (field === 'ticket') val = r.ticket?.ticketId;
      }

      if (typeof val === 'object' && val !== null) {
        if (val.summary) return val.summary;
        if (val.resourceName) return val.resourceName;
        if (val.team) return val.team;
        if (val.ticketId) return val.ticketId;
        if (val.assignmentGroup) return val.assignmentGroup;
        return Array.isArray(val) ? val.join(", ") : JSON.stringify(val);
      }
      return val;
    }
  }
};

  const summary = resp?.severitySummary;

  const allRowsFromResp: PlatformAlertRow[] = resp?.alerts ?? [];
  const filteredRows = useMemo(() => {
    return allRowsFromResp.filter((r) => r.severity !== ("PLATFORM_AFFECTED" as any));
  }, [allRowsFromResp]);

  const rows = useMemo(() => {
    const data = [...filteredRows];
    const { sortField, sortDir } = sortingInfo;

    if (sortDir === "asc" && sortField) {
      data.sort((a, b) => {
        const valA = getSortValue(a, sortField);
        const valB = getSortValue(b, sortField);
        const isMissingA = !valA || valA === "-" || valA === "";
        const isMissingB = !valB || valB === "-" || valB === "";
        if (isMissingA && isMissingB) return 0;
        if (isMissingA) return 1;
        if (isMissingB) return -1;
        return String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return data;
  }, [filteredRows, sortingInfo]);

  useEffect(() => {
    if (resp?.isGrouped && resp.groupedData && resp.groupedData.length > 0) {
      const displayData: any[] = [];
      resp.groupedData.forEach(group => {
        displayData.push({
          ...group,
          isExpanded: false,
          showChildrenCount: 50,
          isParent: true,
          children: group.children || [],
        });
      });
      setGroupedDisplayData(displayData);
    } else {
      setGroupedDisplayData(resp?.alerts || []);
    }
  }, [resp]);

const handleGroupToggle = useCallback((row: any) => {
  setGroupedDisplayData(prevData => {
    // Find the parent index using groupByValue
    const parentIndex = prevData.findIndex((item: any) => 
      item.isParent === true && item.groupByValue === row.groupByValue
    );

    if (parentIndex === -1) return prevData;

    const currentParent = prevData[parentIndex] as GroupedAlertData & { showChildrenCount?: number };
    // Use the actual total children count from the parent's children array
    const totalChildren = currentParent.children.length;
    const showChildrenCount = currentParent.showChildrenCount || 50;
    const hasMoreChildren = totalChildren > showChildrenCount;

    const newData = [...prevData];

    if (!currentParent.isExpanded) {
      // Show up to showChildrenCount children (not just first 50)
      const childrenToShow = currentParent.children.slice(0, showChildrenCount);
      const insertPosition = parentIndex + 1;
      newData.splice(insertPosition, 0, ...childrenToShow);

      if (hasMoreChildren) {
        const showMoreRow: ShowMoreRow = {
          id: `show-more-${currentParent.groupByValue}`,
          isShowMoreRow: true,
          parentGroupValue: currentParent.groupByValue,
          currentShowCount: showChildrenCount,
          totalChildrenCount: totalChildren,
          groupBy: groupBy,
          parentGroup: currentParent,
        };
        newData.splice(insertPosition + childrenToShow.length, 0, showMoreRow);
      }

      newData[parentIndex] = {
        ...currentParent,
        isExpanded: true,
        showChildrenCount: showChildrenCount,
      };
    } else {
      // Collapse: remove children from display
      let rowsToRemove = Math.min(showChildrenCount, totalChildren);
      const hasShowMoreRow = hasMoreChildren;
      if (hasShowMoreRow) {
        rowsToRemove += 1;
      }
      newData.splice(parentIndex + 1, rowsToRemove);
      newData[parentIndex] = {
        ...currentParent,
        isExpanded: false,
        showChildrenCount: showChildrenCount,
      };
    }

    return newData;
  });
}, [groupBy]);


const handleShowMoreClick = useCallback(async (showMoreRow: ShowMoreRow) => {
  const groupValue = showMoreRow.parentGroupValue;
  const currentShowCount = showMoreRow.currentShowCount;

  if (loadingMoreChildren[groupValue]) return;

  setLoadingMoreChildren(prev => ({ ...prev, [groupValue]: true }));
  setLoadingGroupId(`${groupBy}-${groupValue}`);

  try {
    const requestBody = {
      platformId: platformId,
      groupBy: groupBy,
      groupValue: groupValue,
      afterCount: currentShowCount,
      pageSize: 50,
      sortField: sortingInfo.sortField || 'alertTime',
      sortDir: sortingInfo.sortDir || 'desc',
      searchText: searchText,
      severity: severityFilter === "ALL" ? [] : [severityFilter],
      timeRange: timeRangeType === 'today' ? 'today' :
        (timeRangeType === 'custom' && customStartDate && customEndDate)
          ? { startTime: customStartDate, endTime: customEndDate }
          : timeRangeType,
      customStartDate: customStartDate,
      customEndDate: customEndDate,
    };

    const response = await fetchPlatformHealthGroupChildren(requestBody);

    if (response?.success && response.data?.children) {
      // Transform the new children and add a unique key
      const newChildren = transformAlerts(response.data.children, ianaTimezone).map((child, idx) => ({
        ...child,
        _uniqueId: `${groupValue}-${currentShowCount + idx}-${Date.now()}`,
      }));
      
      const hasMore = response.data.hasMore;
      const totalChildrenCount = response.data.totalCount || showMoreRow.totalChildrenCount;

      setGroupedDisplayData(prevData => {
        const rowIndex = prevData.findIndex((item: any) => item.id === showMoreRow.id);
        if (rowIndex === -1) return prevData;

        const parentIndex = prevData.findIndex((item: any) =>
          (item.isParent === true || item.groupByValue) && item.groupByValue === groupValue
        );

        if (parentIndex === -1) return prevData;

        const currentParent = prevData[parentIndex] as GroupedAlertData & { showChildrenCount?: number };
        const newShowCount = currentShowCount + newChildren.length;

        const newData = [...prevData];
        
        // Remove the current Show More row
        newData.splice(rowIndex, 1);
        
        // Insert new children
        const insertPosition = parentIndex + 1 + currentShowCount;
        newData.splice(insertPosition, 0, ...newChildren);
        
        // Insert new Show More row if there are more children
        if (hasMore) {
          const newShowMoreRow: ShowMoreRow = {
            ...showMoreRow,
            id: `show-more-${groupValue}-${newShowCount}`,
            currentShowCount: newShowCount,
            totalChildrenCount: totalChildrenCount,
          };
          newData.splice(insertPosition + newChildren.length, 0, newShowMoreRow);
        }
        
        // Update parent's children array and show count
        const updatedChildren = [...currentParent.children, ...newChildren];
        newData[parentIndex] = {
          ...currentParent,
          showChildrenCount: newShowCount,
          children: updatedChildren,
          groupCount: totalChildrenCount,
          isExpanded: true,
        };
        
        console.log('Updated groupedDisplayData after show more:', newData);
        console.log('Parent children count:', updatedChildren.length);
        console.log('Display rows count:', newData.length);
        
        return newData;
      });
    }
  } catch (error) {
    console.error("Failed to load more children:", error);
  } finally {
    setLoadingMoreChildren(prev => ({ ...prev, [groupValue]: false }));
    setLoadingGroupId(null);
  }
}, [platformId, groupBy, sortingInfo.sortField, sortingInfo.sortDir, searchText, severityFilter, timeRangeType, customStartDate, customEndDate, ianaTimezone]);

const columnsKey = useMemo(() => JSON.stringify(resp?.columnList ?? []), [resp?.columnList]);

const getColumnsWithGrouping = useCallback((baseColumns: any[], groupByValue: string) => {
  if (!groupByValue || groupByValue === 'none') {
    return baseColumns;
  }

  const groupedColumn = {
    key: groupByValue,
    name: groupByOptions.find(opt => opt.value === groupByValue)?.label || groupByValue.toUpperCase(),
    minWidth: 200,  // Minimum width
    width: 350,     // Default width - can grow/shrink
    maxWidth: 450, 
    formatter: ({ row, rowIdx }: any) => {
      if (row.isShowMoreRow) {
        const isLoading = loadingGroupId === `${groupByValue}-${row.parentGroupValue}`;
        return (
          <GroupByShowMore
            key={`show-more-${row.id}-${rowIdx}`}
            row={row}
            handleShowMoreClick={handleShowMoreClick}
            isLoading={isLoading}
          />
        );
      }

      if (row.isParent) {
        return (
          <GroupedTableFormatter
            key={`group-${row.groupByValue}`}
            row={row}
            groupByColumn={groupByValue}
            groupByLabel={row.groupByValue}
            onToggle={handleGroupToggle}
            countLabel="Alerts"
          />
          
        );
      }

      const displayValue = row[groupByValue] || row.groupByValue || '-';
      return (
        <Box sx={{ pl: 6, textAlign: 'left', width: '100%' }} key={`row-${row.id}`}>
          <Typography sx={{ fontWeight: 500, fontSize: '14px', textAlign: 'left' }}>
            {displayValue}
          </Typography>
        </Box>
      );
    },
    resizable: true,
    sortable: true
  };

  const remainingColumns = baseColumns.filter(col => col.key !== groupByValue);
  return [groupedColumn, ...remainingColumns];
}, [groupByOptions, handleGroupToggle, handleShowMoreClick, loadingGroupId]);

  const gridColumns = useMemo(() => {
  let columnsFromAPI: any[] = [];
  try {
    columnsFromAPI = JSON.parse(columnsKey);
  } catch (e) { console.log("getcolumns error",e)}

  
  function getFilterType(type:string) {
    if (type === 'time' || type === 'date') {
      return 'date';
    }

    if (type === 'number') {
      return 'number';
    }

    return 'text';
  }


  const baseColumns = columnsFromAPI.map((col: any) => {
    const sortOptions = col.sortable ? ['Sort Asc - Desc', 'Sort Dec - Asc'] : [];
    let width = col.width || 240;
    let formatter: any;
    let cellType = String(col.type || '').toLowerCase();
    
    // --- ADD DYNAMIC KEY LOGIC (only when not grouped) ---
    let dynamicKey = col.key;
    const detailType = getFilterType(col.type); // 'time' -> 'date', 'number', else 'text'
    columnDetails[col.key] = detailType;        // matches filterField ('alertTime')
    columnDetails[dynamicKey] = detailType;

    if ((!groupBy || groupBy === 'none') && !resp?.isGrouped) {
      const lowerKey = col.key.toLowerCase();
      let isNumericOrSpecial = false;
      
      if (filteredRows && filteredRows.length > 0) {
        for (let i = 0; i < Math.min(filteredRows.length, 5); i++) {
          const rVal = (filteredRows[i] as any)[col.key];
          if (rVal !== undefined && rVal !== null && rVal !== "") {
            const strVal = String(rVal).trim();
            if (/^[<>=!~0-9%]/.test(strVal) || !isNaN(Number(strVal))) {
              isNumericOrSpecial = true;
            }
            break;
          }
        }
      }

      if (cellType === "time" || cellType === "date" || cellType === "datetime" || lowerKey.includes("time") || lowerKey.includes("date")) {
        dynamicKey = `${col.key}_start_time`;
      } else if (cellType === "text" || cellType === "string") {
        if (!isNumericOrSpecial) {
          dynamicKey = `${col.key}_job_name`;
        }
      }
    }
    // --- END OF DYNAMIC KEY LOGIC ---

    switch (cellType) {
      case 'link':
        formatter = ({ row }: any) => {
          const val = row[col.key] || row.alertId || '-';
          const linkUrl = row.alertUrl || row.ticketUrl || '#';
          return (
            <Box
              component="a"
              href={linkUrl}
              target="_blank"
              rel="noreferrer"
              sx={{
                color: colors.table.moreIcon,
                textDecoration: 'none',
                fontWeight: 600,
                cursor: (linkUrl && linkUrl !== '#') ? 'pointer' : 'default',
                display: 'block',
                width: 'fit-content',
                maxWidth: '85%',
                margin: '0 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                '&:hover': { textDecoration: (linkUrl && linkUrl !== '#') ? 'underline' : 'none' },
              }}
            >
              {val}
            </Box>
          );
        };
        break;
      case 'badge':
        formatter = ({ row }: any) => {
          const rawVal = row[col.key] || row.severity || "-";
          return <SeverityBadge severity={String(rawVal)} />;
        };
        break;
      case 'time':
        formatter = ({ row }: any) => <span>{row[col.key] || '-'}</span>;
        break;
      default:
        formatter = ({ row }: any) => {
          let val = row[col.key];
          if (typeof val === 'object' && val !== null) {
            val = Array.isArray(val) ? val.join(", ") : JSON.stringify(val);
          }
          const display = (val !== null && val !== undefined && val !== '') ? String(val) : "-";
          return <OverflowTooltip text={display} />;
        };
        break;
    }

    return {
      key: dynamicKey,  // Use dynamicKey instead of col.key
      name: col.label,
      filterField: col.key,
      isFilterable: col.filterable ?? true,
      filterable: col.filterable ?? true,
      sortable: col.sortable ?? true,
      width,
      sortOptions,
      filterType:  getFilterType (col.type),
      columnDetails,                     
      formatter: withShowMoreHandler(formatter)
    };
  });

  // Apply grouping if enabled
  if (groupBy && groupBy !== 'none' && resp?.isGrouped) {
    return getColumnsWithGrouping(baseColumns, groupBy);
  }

  return baseColumns;
}, [columnsKey, groupBy, resp?.isGrouped, getColumnsWithGrouping, filteredRows]);

const getColumnFilterValues = useCallback(async (field: string) => {
  if (!platformId) return [];

  const timeRange = timeRangeType === "custom" && customStartDate && customEndDate
    ? {
        startTime: moment(customStartDate).tz("America/Chicago").startOf("day").format("MM-DD-YYYY HH:mm:ss"),
        endTime: moment(customEndDate).tz("America/Chicago").endOf("day").format("MM-DD-YYYY HH:mm:ss"),
      }
    : timeRangeType;

  return getPlatformHealthColumnFilterValues({
    platformId,
    timeRange,
    severity: severityFilter === "ALL" ? [] : [toBackendSeverity(severityFilter)],
    columnFilters:columnFiltersPayloadRef.current,
    field,
  });
}, [platformId, timeRangeType, customStartDate, customEndDate, severityFilter]);

const columnFilterController = useColumnFilters({
  columns: gridColumns,
  mode: "server",
  onChange: ({ filterModel, sortModel }) => {
    columnFiltersPayloadRef.current = toColumnFiltersPayload(filterModel, gridColumns);
    const sort = sortModel[0];
    const sortColumn = gridColumns.find((column: any) => column.key === sort?.columnKey);
    const nextSortField = sortColumn?.filterField ?? "";
    const nextSortDir = sort?.direction ?? "";

    setSortingInfo((previous) => ({
      ...previous,
      sortField: nextSortField,
      sortDir: nextSortDir,
    }));
    setPage(1);
    load({ page: 1, sortField: nextSortField, sortDir: nextSortDir });
  },
});

const flatRows = useMemo(() => {
  console.log('flatRows computing, groupedDisplayData length:', groupedDisplayData.length);
  console.log('groupedDisplayData sample:', groupedDisplayData.slice(0, 2));
  
  if (resp?.isGrouped && groupedDisplayData.length > 0) {
    const flattenGroupedRow = (item: any): any => {
      if (item.isParent) {
        return item;
      }
      // Check if it's a child row (has alertId)
      if (item.alertId) {
        return {
          ...item,
          id: item._uniqueId || `${item.alertId || ''}-${item.lastEventTime || item.durationLabel || ''}`,
          alertNo: item.alertId,
          alertUrl: item.alertUrl || '',
          resource: item.resource?.resourceName || item.resource || '-',
          severity: item.severity || '-',
          issue: item.issue?.summary || item.issue || '-',
          description: item.description || '-',
          alertTime: item.durationLabel || item.alertTime || '-',
          time: item.durationLabel || item.alertTime || '-',
          owner: item.owner?.team || item.owner || '-',
          ticket: item.ticket?.ticketId || item.ticket || '-',
          ticketUrl: item.ticket?.ticketUrl || '',
          assignmentGroup: item.owner?.assignmentGroup || item.assignmentGroup || '-',
          resourceGroup: item.resourceGroup || '-',
          subscriptionId: item.subscriptionId || '-',
          subscriptionName: item.subscriptionName || '-',
        };
      }
      return item;
    };
    
    const flattened = groupedDisplayData.map(flattenGroupedRow);
    console.log('Flattened rows count:', flattened.length);
    console.log('Parent rows count:', flattened.filter(r => r.isParent).length);
    console.log('Child rows count:', flattened.filter(r => !r.isParent && r.alertId).length);
    
    return flattened;
  }
  
  // For non-grouped data...
  return rows.map((r, idx) => ({
    ...r,
    id: `${r.alertId || idx}-${(r as any).lastEventTime ?? r.durationLabel ?? ''}-${idx}`,
    alert_number_col: r.alertId,
    alertNo: r.alertId,
    alertUrl: (r as any).alertUrl || r.ticket?.ticketUrl || '',
    resource_name_col: r.resource?.resourceName ?? '-',
    resource: r.resource?.resourceName ?? (r as any).resource ?? '-',
    severity: r.severity,
    issue_name_col: r.issue?.summary ?? '-',
    issue: r.issue?.summary ?? (r as any).issue ?? '-',
    description: r.description ?? '',
    alert_start_time: r.durationLabel && r.durationLabel !== '-' ? r.durationLabel : (r as any).lastEventTime ?? '-',
    time: r.durationLabel && r.durationLabel !== '-' ? r.durationLabel : (r as any).lastEventTime ?? (r as any).alertTime ?? '-',
    alertTime: r.durationLabel && r.durationLabel !== '-' ? r.durationLabel : (r as any).lastEventTime ?? (r as any).alertTime ?? '-',
    owner_name_col: r.owner?.team ?? '-',
    owner: r.owner?.team ?? (r as any).owner ?? '-',
    ticket: r.ticket?.ticketId ?? (r as any).ticket ?? '',
    ticketUrl: r.ticket?.ticketUrl ?? '',
    assignment_group_col: (r.owner as any)?.assignmentGroup ?? '-',
    assignmentGroup: (r.owner as any)?.assignmentGroup ?? '-',
    resourceGroup: r.resourceGroup ?? '-',
    subscriptionId: r.subscriptionId ?? '-',
    subscriptionName: r.subscriptionName ?? '-',
  }));
}, [rows, resp?.isGrouped, groupedDisplayData]);

 const handleGridSorting = (info: { sortBy: string; sortDir: string }) => {
    const colDef = gridColumns.find((c: any) => c.key === info.sortBy);
    const columnLabel = colDef?.name ?? '';
    
    let backendField = info.sortBy || '';
    if (backendField.endsWith('_job_name')) {
        backendField = backendField.replace('_job_name', '');
    } else if (backendField.endsWith('_start_time')) {
        backendField = backendField.replace('_start_time', '');
    }

    if (!info.sortBy || !info.sortDir) {
      setSortingInfo({ sortByLabel: '', sortField: '', sortDir: '' });
      setPage(1);
      load({ page: 1, sortField: '', sortDir: '' });
    } else {
      setSortingInfo({
        sortByLabel: columnLabel,
        sortField: backendField,
        sortDir: info.sortDir as SortDir,
      });
      setPage(1);
      load({ page: 1, sortField: backendField, sortDir: info.sortDir as SortDir });
    }
  };

  /* ── Map sortingInfo to currSorting format for CustomizedDataGrid ── */
  const gridCurrSorting = useMemo(() => {
    if (!sortingInfo.sortField) return { sortBy: '', sortDir: '' };
    
    const matchedCol = gridColumns.find((c: any) => 
        c.key === sortingInfo.sortField || 
        c.key === `${sortingInfo.sortField}_job_name` || 
        c.key === `${sortingInfo.sortField}_start_time`
    );

    return {
      sortBy: matchedCol ? matchedCol.key : '',
      sortDir: sortingInfo.sortDir || '',
    };
  }, [sortingInfo, gridColumns]);

  const gridColumnNames = useMemo(() => gridColumns.map((c: any) => c.name), [gridColumns]);

  const totalItems = resp?.meta.pagination.totalItems ?? 0;
  const totalPages = resp?.meta.pagination.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = page;

  const handlePageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    if (value < 1 || value > totalPages || value === currentPage) return;
    if (sortingInfo.sortField && value > currentPage && value - currentPage > 1) return;
    setPage(value);
    load({ page: value, sortField: sortingInfo.sortField, sortDir: sortingInfo.sortDir });
  };

  const handlepageSizeChange = (event: any) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(1);
    load({ page: 1, pageSize: newSize, sortField: sortingInfo.sortField, sortDir: sortingInfo.sortDir });
  };

  const rawDisplaySeverity = severityFilter === "ALL" ? getHighestSeverity(summary) : severityFilter;
  const displaySeverity = (overrideStatus && severityFilter === "ALL") ? overrideStatus as any : rawDisplaySeverity;
  const effectiveSeverityForBadge = severityFilter === "ALL" ? displaySeverity : severityFilter as any;
  const statusBadgeColor = displaySeverity === SEVERITY.INFO ? "#2e7d32" : severityBadgeColor(effectiveSeverityForBadge);

  let displaySeverityLabel = "";
  if (String(displaySeverity).toUpperCase() === "HEALTHY") {
    displaySeverityLabel = "Healthy";
  } else if (displaySeverity === SEVERITY.INFO) {
    displaySeverityLabel = "Info";
  } else {
    displaySeverityLabel = String(displaySeverity).charAt(0).toUpperCase() + String(displaySeverity).slice(1).toLowerCase();
  }

  const handleExport = async () => {
    if (!resp || !platformId) return;
    handleMenuClose();
    setExporting(true);

    try {
      const baseBody: any = {
        platformId,
        searchText,
        timeRange: timeRangeType,
        page: 1,
        pageSize: 100,
        sortField: sortingInfo.sortField || undefined,
        sortDir: sortingInfo.sortDir || undefined,
        severity: severityFilter === "ALL" ? undefined : [severityFilter as string],
        groupBy: groupBy !== "none" ? groupBy : undefined,
        columnFilters: columnFiltersPayloadRef.current,
      };

      if (timeRangeType === 'today') {
        baseBody.timeRange = {
          startTime: moment.tz('America/Chicago').startOf('day').format('MM-DD-YYYY HH:mm:ss'),
          endTime: moment.tz('America/Chicago').endOf('day').format('MM-DD-YYYY HH:mm:ss'),
        };
      } else if (timeRangeType === 'custom' && customStartDate && customEndDate) {
        baseBody.timeRange = {
          startTime: moment(customStartDate).tz('America/Chicago').startOf('day').format('MM-DD-YYYY HH:mm:ss'),
          endTime: moment(customEndDate).tz('America/Chicago').endOf('day').format('MM-DD-YYYY HH:mm:ss'),
        };
      }

      const initialResponse = await getPlatformHealthDetail(baseBody);
      if (!initialResponse?.success || !initialResponse.data) throw new Error("Failed to fetch initial data");

      let exportRows: any[] = [];
      const isGroupedExport = initialResponse.data.isGrouped && groupBy !== "none";

      if (isGroupedExport && initialResponse.data.detail) {
        for (const group of initialResponse.data.detail) {
          const groupKeys = Object.keys(group).filter(key => key !== 'children' && !key.includes('Count'));
          const groupKey = groupKeys[0];
          if (!groupKey) continue;

          const groupValue = group[groupKey] || 'Unassigned';
          let allChildren = [...(group.children || [])];
          const totalCount = group[`${groupKey}Count`] || group.children?.length || 0;

          if (totalCount > allChildren.length) {
            let afterCount = allChildren.length;
            while (afterCount < totalCount) {
              const childrenBody = {
                platformId, groupBy, groupValue, afterCount, pageSize: 100,
                sortField: sortingInfo.sortField || 'alertTime',
                sortDir: sortingInfo.sortDir || 'desc',
                searchText, severity: severityFilter === "ALL" ? [] : [severityFilter],
                timeRange: timeRangeType === 'today' ? 'today' : timeRangeType,
                customStartDate, customEndDate,
              };
              const childrenResponse = await fetchPlatformHealthGroupChildren(childrenBody);
              if (childrenResponse?.success && childrenResponse.data?.children) {
                const transformedChildren = transformAlerts(childrenResponse.data.children, ianaTimezone);
                allChildren = [...allChildren, ...transformedChildren];
                afterCount += transformedChildren.length;
                if (!childrenResponse.data.hasMore) break;
              } else break;
            }
          } else {
            allChildren = transformAlerts(allChildren, ianaTimezone);
          }

          allChildren.forEach((child: any) => {
            exportRows.push({ ...child, _groupValue: groupValue, _groupField: groupBy });
          });
        }
      } else {
        let allDetails = [...(initialResponse.data.detail || [])];
        const totalItemsExport = initialResponse.data.pagination?.totalItems || 0;
        if (totalItemsExport > allDetails.length) {
          let currentPage = 2;
          const totalPagesExport = Math.ceil(totalItemsExport / 100);
          while (currentPage <= totalPagesExport) {
            const pageBody = { ...baseBody, page: currentPage, pageSize: 100 };
            const pageResponse = await getPlatformHealthDetail(pageBody);
            if (pageResponse?.success && pageResponse.data?.detail) {
              allDetails = [...allDetails, ...pageResponse.data.detail];
            }
            currentPage++;
          }
        }
        exportRows = transformAlerts(allDetails, ianaTimezone).filter(r => r.severity !== "PLATFORM_AFFECTED");
      }

      const isAzureExport = resp?.meta.platform.alertSource === "Azure" || platformName?.toLowerCase().includes("azure");
      const baseHeaders = isAzureExport
        ? ["Alert No", "Severity", "Issue", "Time", "Resource", "Resource Group", "Subscription ID", "Subscription Name"]
        : ["Alert No.", "Resource", "Severity", "Issue", "Description", "Time", "Owner", "Ticket", "Assignment Group"];

      let headers: string[];
      let dataExtractor: (row: any) => any[];

      if (groupBy !== "none" && resp?.isGrouped) {
        const groupColumnLabel = groupByOptions.find(opt => opt.value === groupBy)?.label || groupBy?.toUpperCase() || "GROUP";
        headers = [groupColumnLabel, ...baseHeaders];
        dataExtractor = (row) => {
          const groupValue = row._groupValue || '-';
          const baseData = isAzureExport
            ? [row.alertId || '-', row.severity || '-', row.issue?.summary || '-',
               row.durationLabel || (row as any).lastEventTime || '-', row.resource?.resourceName || '-',
               row.resourceGroup || '-', row.subscriptionId || '-', row.subscriptionName || '-']
            : [row.alertId || '-', row.resource?.resourceName || '-', row.severity || '-',
               row.issue?.summary || '-', row.description || '-', row.durationLabel || (row as any).lastEventTime || '-',
               row.owner?.team || '-', row.ticket?.ticketId || '-', (row.owner as any)?.assignmentGroup || '-'];
          return [groupValue, ...baseData];
        };
      } else {
        headers = baseHeaders;
        dataExtractor = (row) => isAzureExport
          ? [row.alertId || '-', row.severity || '-', row.issue?.summary || '-',
             row.durationLabel || (row as any).lastEventTime || '-', row.resource?.resourceName || '-',
             row.resourceGroup || '-', row.subscriptionId || '-', row.subscriptionName || '-']
          : [row.alertId || '-', row.resource?.resourceName || '-', row.severity || '-',
             row.issue?.summary || '-', row.description || '-', row.durationLabel || (row as any).lastEventTime || '-',
             row.owner?.team || '-', row.ticket?.ticketId || '-', (row.owner as any)?.assignmentGroup || '-'];
      }

      const excelData = [headers, ...exportRows.map(row => dataExtractor(row))];
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Platform Health Alerts");
      XLSX.writeFile(wb, `Platform_Health_Alerts_${platformId}_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExporting(false);
    }
  };

  const handleScreenCapture = async () => {
    try {
      setScreenCapturing(true);
      const element = document.getElementById("ua-capture-area");
      if (!element) return;
      const canvas = await html2canvas(element, { backgroundColor: colors.modal.background, scale: 2, useCORS: true });
      setPreviewImage(canvas.toDataURL("image/png"));
      setOpenPreview(true);
    } catch (err) {
      console.error("Screenshot failed", err);
    } finally {
      setScreenCapturing(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (isPdfGenerating || !platformId) return;
    setIsPdfGenerating(true);
    try {
      // ── Fetch up to 500 rows with current filters ─────────────────────────
      const body = buildAlertRequestBody(
        platformId,
        {
          nextSearch: searchText,
          nextPage: 1,
          nextPageSize: 500,
          nextSortField: sortingInfo.sortField,
          nextSortDir: sortingInfo.sortDir,
          nextSeverity: severityFilter,
          activeSearchAfterMap: {},
          nextGroupBy: '',
          nextColumnFilters: columnFiltersPayloadRef.current,
        },
        { timeRangeType, customStartDate, customEndDate }
      );

      const response = await getPlatformHealthDetail(body);
      if (!response?.success || !response.data) throw new Error("Failed to fetch PDF data");

      const allRows = transformAlerts(response.data.detail, ianaTimezone)
        .filter((r) => r.severity !== ("PLATFORM_AFFECTED" as any));

      // ── Build a formatter map covering ALL possible API columns ──────────
      const fmtSeverity = (r: any): string => String(r?.severity ?? '--').toUpperCase();

      const allPdfColumnsMap: Record<string, PdfColumn> = {
        // Alert identification
        'alertNo':            { key: 'alertNo',            label: 'ALERT NO.',           weight: 20, formatter: r => r?.alertId || r?.alertNo || '--' },
        'alertId':            { key: 'alertId',            label: 'ALERT NO.',           weight: 20, formatter: r => r?.alertId || '--' },
        // Severity
        'severity':           { key: 'severity',           label: 'SEVERITY',            weight: 14, formatter: fmtSeverity, isStatus: true },
        // Issue / description
        'issue':              { key: 'issue',              label: 'ISSUE',               weight: 30, formatter: r => String(r?.issue?.summary ?? r?.issue ?? '--').replace(/[\r\n]+/g, ' ').slice(0, 80) },
        'description':        { key: 'description',        label: 'DESCRIPTION',         weight: 30, formatter: r => String(r?.description ?? '--').replace(/[\r\n]+/g, ' ').slice(0, 80) },
        // Time columns
        'alertTime':          { key: 'alertTime',          label: 'TIME',                weight: 18, formatter: r => r?.durationLabel || r?.alertTime || '--' },
        'startTime':          { key: 'startTime',          label: 'START TIME',          weight: 22, formatter: r => r?.startTime || '--' },
        'endTime':            { key: 'endTime',            label: 'END TIME',            weight: 22, formatter: r => r?.endTime || '--' },
        'slaTime':            { key: 'slaTime',            label: 'SLA TIME',            weight: 18, formatter: r => r?.slaTime || '--' },
        // Owner / assignment
        'owner':              { key: 'owner',              label: 'OWNER',               weight: 18, formatter: r => r?.owner?.team ?? (typeof r?.owner === 'string' ? r?.owner : '--') },
        'assignmentGroup':    { key: 'assignmentGroup',    label: 'ASSIGNMENT GROUP',    weight: 22, formatter: r => r?.owner?.assignmentGroup ?? r?.assignmentGroup ?? '--' },
        // Ticket
        'ticket':             { key: 'ticket',             label: 'TICKET',              weight: 14, formatter: r => r?.ticket?.ticketId ?? (typeof r?.ticket === 'string' ? r?.ticket : '--') },
        // Resource columns
        'resource':           { key: 'resource',           label: 'RESOURCE',            weight: 22, formatter: r => r?.resource?.resourceName ?? (typeof r?.resource === 'string' ? r?.resource : '--') },
        'resourceGroup':      { key: 'resourceGroup',      label: 'RESOURCE GROUP',      weight: 22, formatter: r => r?.resourceGroup || '--' },
        // Subscription columns
        'subscriptionName':   { key: 'subscriptionName',   label: 'SUBSCRIPTION',        weight: 22, formatter: r => r?.subscriptionName || '--' },
        'subscriptionId':     { key: 'subscriptionId',     label: 'SUBSCRIPTION ID',     weight: 22, formatter: r => r?.subscriptionId || '--' },
        // Threshold columns
        'thresholdCondition': { key: 'thresholdCondition', label: 'THRESHOLD CONDITION', weight: 24, formatter: r => r?.thresholdCondition || '--' },
        'thresholdValue':     { key: 'thresholdValue',     label: 'THRESHOLD VALUE',     weight: 20, formatter: r => r?.thresholdValue || '--' },
      };

      // ── Build name→key mapping from the current gridColumns ─────────────
      // gridColumns[].name = col.label (from API columnList), gridColumns[].key = dynamicKey
      // We need label → original API key, so we use resp?.columnList directly
      const apiColumnList: any[] = resp?.columnList ?? [];
      const labelToApiKeyMap: Record<string, string> = {};
      apiColumnList.forEach((col: any) => {
        labelToApiKeyMap[col.label] = col.key;
      });

      // ── Read user's column preferences from localStorage ─────────────────
      const storedPref = localStorage.getItem('PipelineStatus_user_col_pref');
      let preferredColumnNames: string[] | null = null;
      if (storedPref && storedPref !== 'undefined') {
        try {
          preferredColumnNames = JSON.parse(storedPref);
        } catch { preferredColumnNames = null; }
      }

      // Fall back to the full gridColumnNames (all API columns in their default order)
      const columnNames = (preferredColumnNames && Array.isArray(preferredColumnNames) && preferredColumnNames.length > 0)
        ? preferredColumnNames
        : gridColumnNames;

      // ── Build PDF columns in user's preferred order ──────────────────────
      const pdfColumns: PdfColumn[] = columnNames
        .map((colName: string) => {
          // colName is the column label as stored by CustomizedDataGrid
          const apiKey = labelToApiKeyMap[colName];
          if (!apiKey) return undefined;
          const pdfCol = allPdfColumnsMap[apiKey];
          if (!pdfCol) {
            // Fallback: generic text formatter for any unmapped column
            return { key: apiKey, label: colName.toUpperCase(), weight: 18, formatter: (r: any) => String(r?.[apiKey] ?? '--') } as PdfColumn;
          }
          return { ...pdfCol, label: colName.toUpperCase() };
        })
        .filter((col): col is PdfColumn => col !== undefined);

      await generateDashboardPdf({
        columns:           pdfColumns,
        allData:           allRows,
        dashboardTitle:    'Platform Health Alerts Dashboard',
        tableTitle:        effectivePlatformName,
        totalElements:     totalItems,
        topKpiElementId:   'ph-top-kpi',
        fileNamePrefix:    `PlatformHealth_${platformId}`,
      });
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleClose = useCallback(() => {
    setGroupBy("none");
    onClose();
  }, [onClose]);

   const includeGroupByMenuColumns = useMemo(() => {
    if (resp?.columnList && resp.columnList.length > 0) {
      return resp.columnList
        .filter((col: any) => ALL_GROUPABLE_FIELDS.some(f => f.value === col.key))
        .map((col: any) => col.key);
    }
    return ALL_GROUPABLE_FIELDS.map(f => f.value);
  }, [resp?.columnList]);

  const formatCount = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }

    return value.toString();
};


  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        id: "ua-capture-area",
        sx: {
          width: 1920,
          maxWidth: "95vw",
          height: 1080,
          maxHeight: "95vh",
          borderRadius: "4px",
          backgroundColor: `${colors.modal.background} !important`,
          background: `${colors.modal.background} !important`,
          color: "#1a2a3a !important",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
        },
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `${colors.modal.background} !important`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: colors.modal.titleBg,
          px: 3,
          py: "14px",
          minHeight: 48,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "16px",
              color: colors.modal.title,
            }}
          >
            {effectivePlatformName}
          </Typography>

          <Box
            sx={{
              display: "inline-block",
              px: "12px",
              py: "3px",
              borderRadius: "4px",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "12px",
              color: colors.modal.title,
              background: statusPillColor(overrideStatus ?? resp?.meta.platform.status ?? SEVERITY.INFO),
            }}
          >
            {overrideStatus ?? resp?.meta.platform.status ?? SEVERITY.INFO}
          </Box>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{
            color: colors.modal.title,
            fontSize: "20px",
            p: "4px 8px",
            opacity: 0.85,
            transition: "opacity 0.15s",
            "&:hover": { opacity: 1, background: "transparent" },
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        id="ph-top-kpi"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: "26px",
          pb: "22px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: `${colors.modal.background} !important`,
          flexShrink: 0,
          color: "#1a2a3a",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "6px",
            flex: 1,
          }}
        >
          <Typography sx={summaryLabelSx}>Status</Typography>
          <Box
            sx={{
              backgroundColor:
                displaySeverity === SEVERITY.INFO || String(displaySeverity).toUpperCase() === "HEALTHY"
                  ? "#e8f5e9"
                  : "#fde2e4",
              color: statusBadgeColor,
              border:
                severityFilter === "ALL"
                  ? "none"
                  : `1px solid ${severityBadgeColor(severityFilter as any)}`,
              px: 1.5,
              py: 0.5,
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: FONT,
              textTransform: "uppercase",
            }}
          >
            {displaySeverityLabel}
          </Box>
        </Box>

          {[
            { label: SEVERITY.CRITICAL, value: summary?.critical ?? 0 },
            { label: SEVERITY.MAJOR, value: summary?.major ?? 0 },
            { label: SEVERITY.MINOR, value: summary?.minor ?? 0 },
            { label: SEVERITY.WARNING, value: summary?.warning ?? 0 },
            { label: SEVERITY.INFO, value: summary?.info ?? 0 },
          ].map((item) => {
            const color = severityBadgeColor(item.label as any);

            return (
              <Button
                key={item.label}
                disableRipple
                onClick={() => {
                  const nextFilter =
                    severityFilter === item.label
                      ? "ALL"
                      : item.label;

                  setPage(1);

                  load({
                    severityFilter: nextFilter as AlertSeverityFilter,
                    page: 1,
                  });
                }}
                sx={{
                  width: 90,
                  height: 70,
                  p: 0,
                  borderRadius: 0,

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  paddingLeft: "12px",
                  gap: "4px",

                  textTransform: "none",

                  backgroundColor:
                    severityFilter === item.label
                      ? "inherit"
                      : color,

                  color:
                    severityFilter === item.label
                      ? color
                      : colors.modal.title,

                  border:
                    severityFilter === item.label
                      ? "none"
                      : `1px solid ${color}`,

                  borderBottom:
                    severityFilter === item.label
                      ? `3px solid ${color}`
                      : `1px solid ${color}`,

                  "&:hover": {
                    backgroundColor:
                      severityFilter === item.label
                        ? "inherit"
                        : color,

                    color:
                      severityFilter === item.label
                        ? color
                        : colors.modal.title,
                  },
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.2,
                  }}
                >
                  {item.label.charAt(0) + item.label.slice(1).toLowerCase()}
                </span>

                <span
                  style={{
                    fontSize: "26px",
                    fontWeight: 400,
                    lineHeight: 1.1,
                  }}
                >
                  {formatCount(item.value)}
                </span> 
              </Button>
            );
          })}

          {!isAzure && (
            <>
              <Box sx={{ width: 12 }} />

              <Button
                disableRipple
                disableElevation
                sx={{
                  width: 150,
                  height: 70,
                  p: 0,
                  borderRadius: 0,

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  paddingLeft: "12px",                
                  gap: "2px",

                  textTransform: "none",
                  
                  backgroundColor: colors.KpiSeverityWidget.severity.Acknowledged,
                  color: colors.modal.title,
                  cursor: "default",

                  "&:hover": {
                    backgroundColor: colors.KpiSeverityWidget.severity.Acknowledged,
                  },
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.2,
                  }}
                >
                  Acknowledged
                </span>

                <span
                  style={{
                    fontSize: "26px",
                    fontWeight: 400,
                    lineHeight: 1.1,
                  }}
                >
                  {formatCount(summary?.acknowledged ?? 0)}
                </span>
              </Button>

              <Button
                disableRipple
                disableElevation
                sx={{
                  width: 150,
                  height: 70,
                  p: 0,
                  borderRadius: 0,

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  paddingLeft: "12px",                
                  gap: "2px",

                  textTransform: "none",

                  backgroundColor: colors.KpiSeverityWidget.severity.Unacknowledged,
                  color: colors.modal.title,
                  cursor: "default",

                  "&:hover": {
                    backgroundColor: colors.KpiSeverityWidget.severity.Unacknowledged,
                  },
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.2,
                  }}
                >
                  Unacknowledged
                </span>

                <span
                  style={{
                    fontSize: "26px",
                    fontWeight: 400,
                    lineHeight: 1.1,
                  }}
                >
                  {formatCount(summary?.unacknowledged ?? 0)}
                </span>
              </Button>
            </>
          )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          backgroundColor: `${colors.modal.background} !important`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", px: 3, pt: "12px" }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "14px",
              color: colors.modal.sectionTitle,
              whiteSpace: "nowrap",
            }}
          >
            Details
          </Typography>
          <Divider sx={{ flex: 1, borderColor: colors.modal.sectionTitle, opacity: 0.5, borderWidth: "0.4px" }} />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            px: 3,
            pt: "16px",
            pb: "12px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >

          <Box
            sx={{
              flex: 1,
              minHeight: 50,
              borderRadius: "4px",
              border: `0.8px solid ${colors.inputNew.inputBorder}`,
              background: "transparent !important",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: 2,
              opacity: 1,
              "&:hover": { borderColor: colors.inputNew.inputBorderHover },
              "&:focus-within": { borderColor: colors.inputNew.inputBorderFocus },
            }}
          >
            <InputBase
              value={searchDraft}
              placeholder="Search…"
              sx={{
                width: "100%",
                fontFamily: FONT,
                fontSize: "14px",
                color: colors.inputNew.inputText,
                "& input": {
                  color: colors.inputNew.inputText,
                  caretColor: colors.inputNew.inputIcon,
                  "::placeholder": { color: colors.inputNew.inputPlaceholder, opacity: 1 },
                },
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSearchDraft(val);

                if (searchError) setSearchError("");

                if (val.trim().length === 0 && searchText !== "") {
                  setPage(1);
                  setSearchText("");
                  load({ searchText: "", page: 1 });
                }
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;

                const q = searchDraft.trim();

                if (q.length === 0) {
                  setPage(1);
                  setSearchText("");
                  setSearchError("");
                  load({ searchText: "", page: 1 });
                  return;
                }

                if (q.length < 3) {
                  setSearchError("Enter minimum 3 character to search");
                  return;
                }

                setPage(1);
                setSearchText(q);
                setSearchError("");
                load({ searchText: q, page: 1 });
              }}
            />

            {searchError && (
              <Typography
                sx={{
                  mt: "4px",
                  fontFamily: FONT,
                  fontSize: "12px",
                  color: colors.inputNew.error,
                  lineHeight: "14px",
                }}
              >
                {searchError}
              </Typography>
            )}
          </Box>
          
          <PlatformHealthGroupByDropdown
          value={groupBy}
          onChange={handleGroupByChange}
          groupByList={ALL_GROUPABLE_FIELDS}
          includeGroupByMenuColumns={includeGroupByMenuColumns}
          />
          <CaptureExportControls
            onScreenshot={handleScreenCapture}
            screenCapturing={screenCapturing}
            onExport={handleExport}
            isExporting={exporting}
            isVisible={true}
            type="platform"
            sx={{ flexDirection: 'row' }}
            showExport={false}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: "24px",
              height: "24px",
              opacity: 1,
              transition: "background 0.15s",
              borderRadius: "50%",
              "&:hover": { background: colors.DropdownV2.menuItemSelectedBg },
              "& svg path:nth-of-type(1)": { stroke: colors.DropdownV2.dropdownIconDefault, fill: "none" },
              "& svg path:nth-of-type(2)": { fill: colors.DropdownV2.dropdownIconDefault },
            }}
            aria-label="More options"
            onClick={handleMenuClick}
          >
            <MoreOptionsIcon style={{ width: "24px", height: "24px" }} />
          </Box>

          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  width: 220,
                  borderRadius: "6px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.12) !important",
                  backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                  background: `${colors.DropdownV2.menuItemBackground} !important`,
                  color: `${colors.DropdownV2.menuItemText} !important`,
                  "& .MuiList-root": {
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                    padding: 0,
                  },
                  "& .MuiMenuItem-root": {
                    fontFamily: FONT,
                    fontSize: "14px",
                    color: `${colors.DropdownV2.menuItemText} !important`,
                    fontWeight: 600,
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                    "&:hover": {
                      backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
                      color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                      color: `${colors.DropdownV2.menuItemSelectedText} !important`,
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                      color: `${colors.DropdownV2.menuItemSelectedText} !important`,
                    },
                  },
                },
              },
            }}
            MenuListProps={{
              sx: {
                backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                py: 0,
              },
            }}
          >
            <MenuItem onClick={handleExport} disabled={exporting} sx={{ backgroundColor: `${colors.DropdownV2.menuItemBackground} !important` }}>
              {exporting ? (
                <CircularProgress size={20} sx={{ color: `${colors.DropdownV2.dropdownIconDefault} !important` }} />
              ) : (
                <FileDownloadOutlinedIcon sx={{ color: `${colors.DropdownV2.dropdownIconActive} !important`, fontSize: "20px" }} />
              )}
              {exporting ? "Exporting..." : "Export as .xlsx"}
            </MenuItem>

            {sortingInfo.sortField && (
              <>
                <Divider
                  sx={{
                    my: "0 !important",
                    borderColor: "rgba(0,0,0,0.08)",
                    backgroundColor: "rgba(0,0,0,0.08) !important",
                  }}
                />
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setSortingInfo({ sortByLabel: "", sortField: "", sortDir: "" });
                    setSearchAfterMap({});
                    setPage(1);
                    load({ page: 1, sortField: "", sortDir: "" });
                  }}
                  sx={{ backgroundColor: `${colors.DropdownV2.menuItemBackground} !important` }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      "& svg path": { fill: colors.DropdownV2.dropdownIconActive },
                    }}
                  >
                    <ClearSortIcon style={{ width: 24, height: 24 }} />
                  </Box>
                  Clear Sorting
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>

        <Box
          sx={{
            display: "block",
            overflowX: "auto",
            overflowY: "auto",
            flex: 1,
            minWidth: 0,
            pl: 3,
            pr: 2,
            '& > .MuiStack-root': {
              display: 'none !important',
            },
            '& .rdg': {
              background: 'transparent !important',
              border: 'none !important',
              overflowY: 'auto !important',
              overflowX: 'auto !important',
              minWidth: isAzure ? '1920px' : '2160px',
              maxHeight: 'none !important',
              height: '100% !important',
            },
            '& .rdg-header-row .rdg-cell': {
              background: `${colors.table.headerBg} !important`,
              color: `${colors.table.headerText} !important`,
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              borderRight: `2px solid ${colors.table.headerText} !important`,
              padding: '8px 15px 8px 20px !important',
              '&:last-child': {
                borderRight: 'none !important',
              },
              '& > div, & .filter-icon, & .sort-icon': {
                width: '100% !important',
                display: 'flex !important',
                justifyContent: 'space-between !important',
                gap: '8px !important',
                alignItems: 'center !important',
                height: '100% !important',
              },
              '& span': {
                fontWeight: '700 !important',
                textTransform: 'uppercase !important',
                lineHeight: '1 !important',
                display: 'flex !important',
                alignItems: 'center !important',
              },
              '& span[data-header-key="description"] img': {
                display: 'none !important',
              },
            },
            '& .rdg-cell': {
              fontFamily: FONT,
              fontSize: '13px',
              color: colors.table.text,
              textAlign: 'center',
              borderRight: 'none !important',
              borderBottom: '1px solid rgba(0,0,0,0.06) !important',
              padding: '8px 15px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .rdg-row:nth-of-type(odd)': {
              backgroundColor: `${colors.table.oddRowBgColor} !important`,
            },
            '& .rdg-row:nth-of-type(even)': {
              backgroundColor: `${colors.table.evenRowBgColor} !important`,
            },
            '& .rdg-row:hover': {
              backgroundColor: `${colors.table.highlightRowBgColor} !important`,
            },
            '& .rdg-row:hover .rdg-cell': {
              backgroundColor: 'transparent !important',
            },
            '& .rdg .rdg-cell a': {
              color: colors.table.moreIcon,
              textDecoration: 'none',
            },
            '& .rdg .rdg-cell a:hover': {
              color: colors.table.moreIcon,
              textDecoration: 'underline',
            },
            '& .records-msg': {
              fontSize: 0,
              visibility: 'hidden',
              '&::after': {
                content: '"No alerts found"',
                fontSize: '14px',
                visibility: 'visible',
                display: 'block',
                marginTop: '60px',
                fontWeight: 500,
                color: '#5a6a7e',
              },
            },
          }}
        >
          <CustomizedDataGrid
            title=""
            tableColumns={gridColumns}
            tableData={flatRows}
            tableType="PlatformHealth"
            defaultColumnNames={gridColumnNames}
            rowHeight={50}
            headerRowHeight={50}
            onSorting={handleGridSorting}
            onFilterCheck={() => {}}
            onFilterClick={() => {}}
            onFilterSearch={() => {}}
            currSorting={gridCurrSorting}
            currFilter={{}}
            filterDataOptions={{}}
            isFetching={false}
            isRenderPagination={false}
            isShowMoreButton={false}
            isSortIcon={true}
            enableColumnFilter
            columnFilterController={columnFilterController}
            getColumnFilterValues={getColumnFilterValues}
            style={{
              height: '100%',
              maxHeight: 'none',
              width: 'max-content',
              minWidth: '100%',
              minHeight: '100px',
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          "& > .MuiBox-root": {
            backgroundColor: `${colors.modal.background} !important`,
            borderTop: "1px solid #dae4e4",
          },
          "& .MuiTypography-root": {
            color: `${colors.table.pagination.text} !important`,
          },
          "& .MuiSelect-select": {
            color: `${colors.DropdownV2.menuTextColor} !important`,
          },
          "& .MuiSelect-select.MuiSelect-select": {
            color: `${colors.DropdownV2.menuTextColor} !important`,
          },
          "& .MuiInputBase-root": {
            color: `${colors.DropdownV2.menuTextColor} !important`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-icon": {
            color: `${colors.DropdownV2.dropdownIconDefault} !important`,
          },
          "& .MuiSvgIcon-root": {
            color: `${colors.table.pagination.arrow} !important`,
          },
          "& .MuiPaginationItem-root": {
            color: `${colors.table.pagination.text} !important`,
          },
          "& .MuiPaginationItem-root.Mui-selected": {
            backgroundColor: `${colors.table.pagination.selectedBackground} !important`,
            color: `${colors.table.pagination.selectedText} !important`,
          },
          "& .MuiPaginationItem-root.Mui-selected:hover": {
            backgroundColor: `${colors.table.pagination.selectedBackground} !important`,
          },
          "& .MuiPaginationItem-root:hover": {
            backgroundColor: colors.table.pagination.hoverBackground,
          },
        }}
      >
        <PaginationDetails
          page={page}
          pageSize={String(pageSize)}
          paginationData={{ totalElements: totalItems, totalPages: totalPages }}
          listLength={totalItems}
          tableType="PlatformHealthModal"
          handlepageSizeChange={handlepageSizeChange}
          handlePageChange={handlePageChange}
          isPrevNextPagination
          showGoToPage={false}
          pageSizeOptions={[5, 10, 25, 50]}
          disablePageJumps={!!sortingInfo.sortField}
        />
      </Box>
    </Dialog>


    <ImagePreview
      open={openPreview}
      onClose={() => setOpenPreview(false)}
      image={previewImage}
      fileName={`Platform_Health_Alerts_${platformId}_Dashboard.png`}
      title={'Platform Health Alerts Dashboard'}
      hasDownloadPDFButton
      handleDownloadPDF={handleDownloadPDF}
      isPdfGenerating={isPdfGenerating}
    />
    </>
  );
}
